import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, SkipBack, SkipForward, X } from "lucide-react";
import { useHub } from "../lib/hubContent";
import { AddItemButton, EditItemButton, EditSectionButton } from "./HubEditor";
import SectionHeading from "./SectionHeading";
import Reveal from "./Reveal";

export default function MusicArchive() {
  const hub = useHub();
  const c = hub.section("music");
  const songs = hub.collection("songs") || [];
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [lyricsFor, setLyricsFor] = useState(null);
  const song = songs[Math.min(index, Math.max(songs.length - 1, 0))];

  const step = (dir) => {
    setIndex((i) => (i + dir + songs.length) % songs.length);
  };

  return (
    <section id="music" data-testid="music-section" className="relative bg-[#1A1410] text-zinc-50 py-24 md:py-32 overflow-hidden">
      <EditSectionButton sectionKey="music" label="Edit copy" />
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <SectionHeading
          eyebrow={c.eyebrow}
          title={<>Music, kept like <span className="italic text-rose-500">records.</span></>}
          note={c.note}
          noteColor="text-en-yellow"
        />

        {!song ? (
          <p className="text-white/50 font-mono text-sm">No tracks yet{hub.editMode ? " — add one below." : "."}</p>
        ) : (
        <div className="grid lg:grid-cols-5 gap-14 items-start">
          {/* player */}
          <Reveal className="lg:col-span-2">
            <div className="max-w-sm mx-auto">
              <div className="relative aspect-square">
                <div className={`absolute inset-0 rounded-full bg-gradient-to-br ${song.gradient} blur-2xl opacity-30 transition-opacity`} aria-hidden="true" />
                <div
                  data-testid="vinyl-disc"
                  className="relative w-full h-full rounded-full animate-spin-slow border border-white/10"
                  style={{
                    animationDuration: "5s",
                    animationPlayState: playing ? "running" : "paused",
                    background: "repeating-radial-gradient(circle at center, #101013 0px, #101013 2px, #1b1b1f 2px, #1b1b1f 4px)",
                  }}
                >
                  <div className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[38%] h-[38%] rounded-full bg-gradient-to-br ${song.gradient} flex items-center justify-center shadow-inner`}>
                    <span className="w-3 h-3 rounded-full bg-[#1A1410] border border-white/30" />
                  </div>
                </div>
              </div>

              <div className="mt-8 flex items-center justify-between">
                <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/50">
                  {playing ? "Now spinning" : "On the turntable"} — {song.title}
                </p>
                <p className="font-mono text-[10px] text-white/40">{song.length}</p>
              </div>

              <div className="mt-3 flex items-end gap-1 h-8" data-testid="music-waveform" aria-hidden="true">
                {Array.from({ length: 28 }).map((_, i) => (
                  <span
                    key={i}
                    className={`flex-1 rounded-sm bg-rose-500/80 origin-bottom ${playing ? "animate-eq" : "scale-y-[0.25] opacity-40"}`}
                    style={{ animationDelay: `${(i % 7) * 0.12}s`, animationDuration: `${0.8 + (i % 5) * 0.15}s` }}
                  />
                ))}
              </div>

              <div className="mt-6 flex items-center justify-center gap-5">
                <button data-testid="music-prev-button" onClick={() => step(-1)} aria-label="Previous song" className="w-11 h-11 rounded-full border border-white/20 flex items-center justify-center hover:bg-white/10 transition-colors">
                  <SkipBack size={17} />
                </button>
                <button
                  data-testid="music-play-button"
                  onClick={() => setPlaying((p) => !p)}
                  aria-label={playing ? "Pause" : "Play"}
                  aria-pressed={playing}
                  className="w-16 h-16 rounded-full bg-rose-500 flex items-center justify-center hover:scale-105 transition-transform shadow-[0_0_40px_rgba(244,63,94,0.4)]"
                >
                  {playing ? <Pause size={22} fill="currentColor" /> : <Play size={22} fill="currentColor" className="ml-1" />}
                </button>
                <button data-testid="music-next-button" onClick={() => step(1)} aria-label="Next song" className="w-11 h-11 rounded-full border border-white/20 flex items-center justify-center hover:bg-white/10 transition-colors">
                  <SkipForward size={17} />
                </button>
              </div>
              <p className="mt-6 text-center font-hand text-xl text-white/40 rotate-[-1deg]">{c.player_caption}</p>
            </div>
          </Reveal>

          {/* tracklist */}
          <div className="lg:col-span-3">
            <Reveal delay={0.1}>
              <div className="rounded-3xl border border-white/10 overflow-hidden divide-y divide-white/10 bg-white/[0.03]">
                {songs.map((s, i) => {
                  const isActive = i === index;
                  return (
                    <div
                      key={s.id}
                      data-testid={`track-row-${s.id}`}
                      className={`flex items-center gap-4 md:gap-6 p-5 md:p-6 transition-colors ${isActive ? "bg-white/[0.06]" : "hover:bg-white/[0.04]"}`}
                    >
                      <button
                        onClick={() => setIndex(i)}
                        className="flex items-center gap-4 md:gap-6 flex-1 text-left min-w-0"
                        aria-label={`Select ${s.title}`}
                      >
                        <span className={`font-mono text-xs ${isActive ? "text-rose-400" : "text-white/30"}`}>{String(i + 1).padStart(2, "0")}</span>
                        <span className={`w-10 h-10 md:w-12 md:h-12 rounded-lg bg-gradient-to-br ${s.gradient} shrink-0`} aria-hidden="true" />
                        <span className="min-w-0">
                          <span className={`block font-display font-bold text-lg md:text-2xl truncate ${isActive ? "text-rose-400" : ""}`}>{s.title}</span>
                          <span className="block font-mono text-[10px] tracking-[0.2em] uppercase text-white/40 mt-1">{s.mood} · {s.year}</span>
                        </span>
                      </button>
                      <div className="flex items-center gap-2 shrink-0">
                        <EditItemButton kind="songs" item={s} />
                        <button
                          data-testid={`lyrics-button-${s.id}`}
                          onClick={() => setLyricsFor(s)}
                          className="rounded-full border border-white/20 px-4 py-2 font-mono text-[10px] tracking-[0.2em] uppercase hover:border-rose-400 hover:text-rose-400 transition-colors"
                        >
                          Lyrics
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-5">
                <AddItemButton kind="songs" label="Add a track" className="text-white/60" />
              </div>
              <p className="mt-6 font-mono text-[10px] tracking-[0.25em] uppercase text-white/35">
                {c.tracklist_note}
              </p>
            </Reveal>
          </div>
        </div>
        )}
      </div>

      {/* lyrics modal */}
      <AnimatePresence>
        {lyricsFor && (
          <motion.div
            data-testid="lyrics-modal"
            role="dialog"
            aria-modal="true"
            aria-label={`Lyrics — ${lyricsFor.title}`}
            className="fixed inset-0 z-[70] flex items-center justify-center p-5"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={() => setLyricsFor(null)} />
            <motion.div
              initial={{ opacity: 0, y: 40, rotate: -1 }}
              animate={{ opacity: 1, y: 0, rotate: 0 }}
              exit={{ opacity: 0, y: 24 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="relative max-w-lg w-full rounded-3xl bg-zinc-950 border border-white/15 p-9 md:p-12 overflow-hidden"
            >
              <div className={`absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r ${lyricsFor.gradient}`} aria-hidden="true" />
              <button
                data-testid="lyrics-close-button"
                onClick={() => setLyricsFor(null)}
                aria-label="Close lyrics"
                className="absolute top-5 right-5 w-9 h-9 rounded-full border border-white/20 flex items-center justify-center hover:bg-white/10 transition-colors"
              >
                <X size={16} />
              </button>
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/40 mb-2">Lyrics — excerpt</p>
              <h3 className="font-display font-black text-3xl mb-2">{lyricsFor.title}</h3>
              <p className="font-hand text-xl text-rose-400 mb-7">{lyricsFor.note}</p>
              <div className="space-y-2.5">
                {lyricsFor.lyrics.map((l) => (
                  <p key={l} className="font-display italic text-lg md:text-xl text-white/85 leading-relaxed">{l}</p>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
